import ChartLoader from "../Common/ChartLoader";
import "../Common/skeleton.css";
import "./CoTrader.css";

// Loading placeholders for the desktop CoTrader desk. Each one reuses the
// real component's layout classes, so nothing shifts when data arrives.

export const Bar = ({ width, height = 10, radius, style }) => (
  <div
    className="sk"
    style={{ width, height, borderRadius: radius, flex: "0 0 auto", ...style }}
  />
);

export const SentimentSkeleton = () => (
  <div className="ct-sentiment" aria-hidden="true">
    <div>
      <Bar width={112} />
      <Bar width={76} height={26} style={{ marginTop: 8 }} />
    </div>
    <div>
      <Bar width="100%" height={4} radius={999} />
      <div className="ct-sentiment-scale">
        <Bar width={58} />
        <Bar width={52} />
        <Bar width={54} />
      </div>
    </div>
    <div className="ct-sentiment-meta">
      <Bar width={112} height={24} radius={999} />
      <Bar width={150} />
    </div>
  </div>
);

export const FeedSkeleton = ({ count = 4 }) =>
  Array.from({ length: count }, (_, key) => (
    <div
      className="ct-update ct-update--skeleton"
      key={key}
      aria-hidden="true"
      style={{ animation: "none" }}
    >
      <div className="ct-update-head">
        <span className="ct-update-kind">
          <Bar width={22} height={22} radius={6} />
          <Bar width={key % 2 ? 96 : 118} />
        </span>
        <Bar width={52} />
      </div>
      <Bar width={key % 2 ? "58%" : "74%"} height={14} style={{ marginTop: 12 }} />
      {key % 3 !== 1 && (
        <>
          <Bar width="96%" style={{ marginTop: 12 }} />
          <Bar width="82%" style={{ marginTop: 8 }} />
        </>
      )}
      <Bar width={key % 3 === 1 ? 84 : 132} height={11} style={{ marginTop: 14 }} />
    </div>
  ));

export const SuggestionsSkeleton = ({ count = 4 }) => (
  <div className="ct-suggestions" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <div className="ct-suggestion" key={index}>
        <div className="ct-suggestion-skeleton">
          <Bar width="88%" />
          <Bar width="72%" />
          <Bar width={index % 2 ? "42%" : "54%"} />
        </div>
      </div>
    ))}
  </div>
);

// Ticker tape: a row of symbol / price / change chips.
export const TickerTapeSkeleton = ({ count = 8 }) => (
  <div className="ct-tape-skeleton" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <div className="ct-tape-skeleton-item" key={index}>
        <Bar width={index % 2 ? 54 : 64} height={11} />
        <Bar width={58} height={11} />
        <Bar width={40} height={11} />
      </div>
    ))}
  </div>
);

// Selection bar: asset / instrument pickers on the left, chart controls right.
export const FilterBarSkeleton = () => (
  <div className="ct-filter-skeleton" aria-hidden="true">
    <div className="ct-filter-skeleton-group">
      {[120, 150, 170].map((width) => (
        <div key={width}>
          <Bar width={56} height={8} />
          <Bar width={width} height={32} radius={8} style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
    <div className="ct-filter-skeleton-group">
      <Bar width={92} height={32} radius={8} />
      <Bar width={92} height={32} radius={8} />
      <Bar width={36} height={32} radius={8} />
    </div>
  </div>
);

export const ChartSkeleton = () => (
  <div className="ct-panel ct-chart-skeleton" aria-hidden="true">
    <div className="ct-chart-skeleton-head">
      <Bar width={120} height={16} />
      <Bar width={84} height={12} />
    </div>
    <div className="ct-chart-skeleton-plot" style={{ position: "relative" }}>
      <Bar width="100%" height="100%" radius={10} />
      <ChartLoader />
    </div>
  </div>
);

export const InsightsPanelSkeleton = ({ tabCount = 2 }) => (
  <section className="ct-panel ct-insights" aria-hidden="true">
    <div className="ct-tabs">
      {Array.from({ length: tabCount }, (_, index) => (
        <div className="ct-tab" key={index}>
          <Bar width={index ? 92 : 64} height={14} />
        </div>
      ))}
    </div>
    <SentimentSkeleton />
    <div className="ct-feed">
      <FeedSkeleton count={3} />
    </div>
  </section>
);

export const ChatPanelSkeleton = () => (
  <div className="ct-panel" aria-hidden="true">
    <div className="ct-chat-skeleton-head">
      <Bar width={124} height={16} />
      <Bar width={220} height={10} style={{ marginTop: 8 }} />
    </div>
    <div className="ct-chat-skeleton-body">
      <SuggestionsSkeleton />
    </div>
    <div className="ct-chat-skeleton-composer">
      <Bar width="100%" height={52} radius={16} />
    </div>
  </div>
);

// The whole desk (chart over insights | chat), shown while the instrument
// universe loads and before a product shell can mount.
export const DeskSkeleton = () => (
  <div className="ct-desk-skeleton" role="status" aria-label="Loading CoTrader">
    <div className="ct-desk-skeleton-chart">
      <ChartSkeleton />
    </div>
    <div className="ct-desk-skeleton-row">
      <InsightsPanelSkeleton />
      <ChatPanelSkeleton />
    </div>
  </div>
);

// Full CoTrader page (ticker tape, selection bar, desk). Used by the global
// refresh skeleton so a reload lands on the same layout the page renders.
export const CoTraderPageSkeleton = () => (
  <div className="ct-page w-100 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
    <div className="px-3 pt-3 pb-2">
      <div className="ct-chrome px-3 py-2">
        <TickerTapeSkeleton />
      </div>
    </div>
    <div className="px-3 pb-2">
      <div className="ct-chrome px-3 py-2">
        <FilterBarSkeleton />
      </div>
    </div>
    <DeskSkeleton />
  </div>
);
